import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useMutation, useQuery } from 'convex/react';
import { Check, Loader2, Star, X } from 'lucide-react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import type { Id } from '@bill-alps/backend/convex/_generated/dataModel';
import {
  FEEDBACK_TAGS_HIGH,
  FEEDBACK_TAGS_LOW,
} from '@bill-alps/backend/features/feedback/validators';
import { Button, cn, toast } from '@bill-alps/ui';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useTranslation, useLanguage } from '../../i18n/useTranslation';
import { track } from '@/lib/analytics/track';
import {
  hasFeedbackShownThisSession,
  isFeedbackSnoozed,
  isFirstConnection,
  markConnected,
  markFeedbackShownThisSession,
  migrateLegacyDismissal,
  snoozeFeedback,
  type SnoozeKind,
} from './lib/feedback-prompt';

/** Delay before the prompt appears for a returning user. */
const SHOW_DELAY_MS = 120_000;
/** Longer delay on the user's very first connection (3 min). */
const FIRST_CONNECTION_DELAY_MS = 180_000;
/** How long the thank-you state stays visible before the card hides. */
const DONE_DURATION_MS = 7000;
/** Coalesce rapid rating changes (arrow keys) into a single mutation. */
const PERSIST_DEBOUNCE_MS = 350;
/** Ratings at or below this get the "what went wrong" branch. */
const LOW_RATING_MAX = 3;

/** Public review page opened from the thank-you state on 4-5 stars. */
const REVIEW_URL = 'https://g.page/r/CYcJk_uxQpCKEAI/review';

/** Routes where the prompt must never appear (onboarding / post-signup upsell). */
const EXCLUDED_PATH_PREFIXES = ['/onboarding', '/welcome'];

type Phase = 'ask' | 'detail' | 'sending' | 'done';

function StarRating({
  value,
  hover,
  onHover,
  onChange,
  starLabel,
}: {
  value: number;
  hover: number;
  onHover: (star: number) => void;
  onChange: (rating: number) => void;
  starLabel: (count: number) => string;
}) {
  const active = hover || value;
  const groupRef = useRef<HTMLDivElement>(null);

  const move = (star: number, delta: number) => {
    const next = Math.min(5, Math.max(1, (value || star) + delta));
    onChange(next);
    groupRef.current
      ?.querySelector<HTMLButtonElement>(`[data-star="${next}"]`)
      ?.focus();
  };

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-labelledby="feedback-prompt-title"
      className="mx-auto flex w-fit gap-0.5"
      onMouseLeave={() => onHover(0)}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          role="radio"
          data-star={star}
          aria-checked={star === value}
          aria-label={starLabel(star)}
          tabIndex={
            value === 0 ? (star === 1 ? 0 : -1) : star === value ? 0 : -1
          }
          onMouseEnter={() => onHover(star)}
          onClick={() => onChange(star)}
          onKeyDown={(event) => {
            const delta =
              event.key === 'ArrowRight'
                ? 1
                : event.key === 'ArrowLeft'
                  ? -1
                  : 0;
            if (!delta) return;
            event.preventDefault();
            move(star, delta);
          }}
          className={cn(
            'grid h-11 w-11 cursor-pointer place-items-center rounded-xl transition-all duration-150 ease-spring',
            'hover:bg-riso-amber-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'active:scale-90 motion-reduce:transition-none',
            star <= active ? 'text-riso-amber' : 'text-muted-foreground/40',
            star <= value && 'scale-105',
          )}
        >
          <Star
            className={cn(
              'h-[30px] w-[30px]',
              star <= active ? 'fill-current' : 'fill-transparent',
            )}
            strokeWidth={1.8}
          />
        </button>
      ))}
    </div>
  );
}

export function FeedbackPrompt() {
  const { user } = useCurrentUser();
  const { language } = useLanguage();
  const { t } = useTranslation('feedback');
  const { pathname } = useLocation();

  const hasUser = !!user;
  const onExcludedRoute = EXCLUDED_PATH_PREFIXES.some((p) =>
    pathname.startsWith(p),
  );

  const hasSubmitted = useQuery(
    api.features.feedback.ratings.hasSubmitted,
    hasUser && !onExcludedRoute ? {} : 'skip',
  );
  const submitRating = useMutation(api.features.feedback.ratings.submitRating);
  const addDetails = useMutation(api.features.feedback.ratings.addDetails);

  const [visible, setVisible] = useState(false);
  const [phase, setPhase] = useState<Phase>('ask');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  /** Star currently under the pointer — drives the preview label, 0 when none. */
  const [hoveredStar, setHoveredStar] = useState(0);
  /** Pointer anywhere on the card — pauses the thank-you countdown. */
  const [hovered, setHovered] = useState(false);

  // Rating persistence: one row per prompt, patched on every re-pick. The
  // mutations are chained so a second pick never races the first insert.
  const feedbackIdRef = useRef<Id<'feedback'> | null>(null);
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const snoozeKind: SnoozeKind = hasSubmitted ? 'submitted' : 'never-submitted';

  // Schedule the prompt after an authenticated user is loaded. Depends only on
  // user-presence (not the user object) so a data refresh won't reset the timer;
  // `hasSubmitted` settles once (undefined → boolean) and re-arms the timer.
  // StrictMode-safe: the timer is cleared on cleanup and re-armed on re-run.
  useEffect(() => {
    if (!hasUser || onExcludedRoute || hasSubmitted === undefined) return;

    migrateLegacyDismissal(hasSubmitted ? 'submitted' : 'never-submitted');
    if (isFeedbackSnoozed() || hasFeedbackShownThisSession()) return;

    const delay = isFirstConnection()
      ? FIRST_CONNECTION_DELAY_MS
      : SHOW_DELAY_MS;
    const timer = setTimeout(() => {
      markFeedbackShownThisSession();
      markConnected();
      setVisible(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [hasUser, onExcludedRoute, hasSubmitted]);

  const hideForSession = useCallback(() => {
    setVisible(false);
    track('feedback_dismissed', { method: 'close' });
  }, []);

  // Escape closes for this session only — same promise as the X button.
  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hideForSession();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [visible, hideForSession]);

  // Thank-you auto-close, paused while the pointer rests on the card (the drain
  // bar pauses with it, so the countdown and the bar stay in sync).
  const remainingRef = useRef(DONE_DURATION_MS);
  const startedAtRef = useRef(0);
  useEffect(() => {
    if (phase !== 'done') {
      remainingRef.current = DONE_DURATION_MS;
      return;
    }
    if (hovered) {
      remainingRef.current -= Date.now() - startedAtRef.current;
      return;
    }
    startedAtRef.current = Date.now();
    const timer = setTimeout(
      () => setVisible(false),
      Math.max(0, remainingRef.current),
    );
    return () => clearTimeout(timer);
  }, [phase, hovered]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  if (!visible || onExcludedRoute) return null;

  const isLow = rating <= LOW_RATING_MAX;
  const tagKeys = isLow ? FEEDBACK_TAGS_LOW : FEEDBACK_TAGS_HIGH;
  /** Hovering previews that star's label; otherwise the picked one shows. */
  const activeRating = hoveredStar || rating;

  const persistRating = (value: number) => {
    chainRef.current = chainRef.current.then(async () => {
      try {
        feedbackIdRef.current = await submitRating({
          rating: value,
          locale: language,
          id: feedbackIdRef.current ?? undefined,
        });
      } catch {
        toast.error(t('error'));
      }
    });
    return chainRef.current;
  };

  const flushRating = async (value: number) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
      persistRating(value);
    }
    await chainRef.current;
  };

  const handlePick = (value: number) => {
    if (value !== rating) {
      setTags([]);
      track('feedback_rating_selected', { rating: value });
    }
    setRating(value);
    setPhase('detail');

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      persistRating(value);
    }, PERSIST_DEBOUNCE_MS);
  };

  const toggleTag = (tag: string) => {
    setTags((current) =>
      current.includes(tag)
        ? current.filter((item) => item !== tag)
        : [...current, tag],
    );
  };

  const handleLater = () => {
    snoozeFeedback(snoozeKind);
    setVisible(false);
    track('feedback_dismissed', {
      method: 'later',
      months: snoozeKind === 'submitted' ? 12 : 6,
    });
  };

  const handleSend = async () => {
    setPhase('sending');
    await flushRating(rating);

    const id = feedbackIdRef.current;
    if (!id) {
      toast.error(t('error'));
      setPhase('detail');
      return;
    }

    try {
      await addDetails({
        id,
        comment: comment.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
      });
      track('feedback_submitted', {
        rating,
        hasComment: !!comment.trim(),
        tagCount: tags.length,
      });
      snoozeFeedback('submitted');
      setPhase('done');
    } catch {
      toast.error(t('error'));
      setPhase('detail');
    }
  };

  const revealed = phase === 'detail' || phase === 'sending';

  return (
    <div
      role="dialog"
      aria-labelledby="feedback-prompt-title"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="animate-toast-enter fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-card p-[18px] shadow-hard-lg motion-reduce:animate-none"
    >
      {phase === 'done' ? (
        <div className="px-1 pb-1 pt-1.5 text-center">
          <div className="animate-check-pop mx-auto mb-2.5 grid h-11 w-11 place-items-center rounded-full bg-riso-teal-light text-riso-teal motion-reduce:animate-none">
            <Check className="h-[22px] w-[22px]" strokeWidth={2.5} />
          </div>
          <h2
            id="feedback-prompt-title"
            className="mb-1 font-display font-semibold text-foreground"
          >
            {t(isLow ? 'doneTitleLow' : 'doneTitleHigh')}
          </h2>
          <p className="mb-3.5 text-sm leading-snug text-muted-foreground">
            {t(isLow ? 'doneBodyLow' : 'doneBodyHigh')}
          </p>
          {isLow ? (
            <Link
              to="/support"
              onClick={() => {
                track('feedback_cta_clicked', { type: 'support' });
                setVisible(false);
              }}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-shadow hover:shadow-hard-sm"
            >
              {t('ctaSupport')}
            </Link>
          ) : (
            <a
              href={REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => track('feedback_cta_clicked', { type: 'review' })}
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground transition-shadow hover:shadow-hard-sm"
            >
              {t('ctaReview')}
            </a>
          )}
          <span
            className={cn(
              'animate-drain absolute inset-x-0 bottom-0 h-[3px] origin-left bg-riso-teal motion-reduce:animate-none',
              hovered && '[animation-play-state:paused]',
            )}
          />
        </div>
      ) : (
        <>
          <button
            type="button"
            aria-label={t('close')}
            onClick={hideForSession}
            className="absolute right-2.5 top-2.5 grid h-[30px] w-[30px] cursor-pointer place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-3 pr-6">
            <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-xl border border-border bg-riso-blue-light">
              <img
                src="/mountain-peak.svg"
                alt=""
                aria-hidden="true"
                className="w-full"
              />
            </span>
            <div>
              <h2
                id="feedback-prompt-title"
                className="font-display font-semibold leading-snug tracking-tight text-foreground"
              >
                {t('title')}
              </h2>
              <p className="mt-1 text-sm leading-snug text-muted-foreground">
                {t('description')}
              </p>
            </div>
          </div>

          <div className="mt-3.5">
            <StarRating
              value={rating}
              hover={hoveredStar}
              onHover={setHoveredStar}
              onChange={handlePick}
              starLabel={(count) => t('starLabel', { count })}
            />
          </div>

          {/* Columns mirror the star row exactly, so each label sits on its
              star's centre line. */}
          <div
            aria-live="polite"
            className="mx-auto mt-0.5 flex min-h-4 w-fit gap-0.5"
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <span key={star} className="relative w-11">
                {star === activeRating && (
                  <span className="absolute left-1/2 top-0 -translate-x-1/2 whitespace-nowrap text-[11px] font-semibold text-riso-amber">
                    {t(`ratingLabel.${star}`)}
                  </span>
                )}
              </span>
            ))}
          </div>

          <div
            inert={!revealed}
            className={cn(
              'grid transition-[grid-template-rows] duration-300 ease-spring motion-reduce:transition-none',
              revealed ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            )}
          >
            <div className="min-h-0 overflow-hidden">
              <div
                className={cn(
                  'mt-3.5 border-t border-dashed border-border pt-3.5 transition-opacity duration-200 ease-spring motion-reduce:transition-none',
                  revealed ? 'opacity-100' : 'opacity-0',
                )}
              >
                <p className="mb-2.5 text-sm font-semibold text-foreground">
                  {t(isLow ? 'questionLow' : 'questionHigh')}
                </p>

                <div className="flex flex-wrap gap-2">
                  {tagKeys.map((tag) => {
                    const selected = tags.includes(tag);
                    return (
                      <button
                        key={tag}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => toggleTag(tag)}
                        className={cn(
                          'cursor-pointer rounded-full border px-3 py-1.5 text-sm transition-all duration-150 ease-spring',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
                          selected
                            ? 'border-riso-blue/35 bg-riso-blue-light font-semibold text-riso-blue'
                            : 'border-border bg-card text-foreground hover:border-foreground/30',
                        )}
                      >
                        {t(`${isLow ? 'tagsLow' : 'tagsHigh'}.${tag}`)}
                      </button>
                    );
                  })}
                </div>

                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t(
                    isLow ? 'commentPlaceholderLow' : 'commentPlaceholderHigh',
                  )}
                  rows={2}
                  className="mt-3 w-full resize-none rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground transition-colors placeholder:text-muted-foreground focus-visible:border-riso-blue/55 focus-visible:bg-card focus-visible:outline-none"
                />
              </div>
            </div>
          </div>

          <div className="mt-3.5 flex items-center justify-between gap-2.5">
            <button
              type="button"
              onClick={handleLater}
              className="cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            >
              {t('later')}
            </button>
            {revealed && (
              <Button
                variant="accent"
                size="sm"
                onClick={handleSend}
                disabled={phase === 'sending'}
              >
                {phase === 'sending' ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                    {t('sending')}
                  </>
                ) : (
                  t('submit')
                )}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
